import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Collapsible Low-Scoring Papers Section
 *
 * Displays low-scoring papers (score ≤ 0.5) in a collapsible section
 * Props:
 * - papers: Array of paper objects with relevance_score ≤ 0.5
 * - renderPaper: Function to render each paper card (paper, index) => ReactElement
 */
const CollapsibleLowScoringSection = ({ papers, renderPaper }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!papers || papers.length === 0) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="collapsible-low-scoring-section">
      {/* Expand/Collapse Button */}
      <button
        onClick={toggleExpanded}
        className="collapsible-button w-full"
        aria-expanded={isExpanded}
        aria-controls="low-scoring-papers"
      >
        <div className="flex items-center justify-center gap-3">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}

          <span className="font-medium">
            {isExpanded
              ? t("lowScoringPapers.hide", "Hide low-scoring papers")
              : t("lowScoringPapers.show", {
                  defaultValue:
                    "Show {{count}} low-scoring papers (score ≤ 0.50)",
                  count: papers.length,
                })}
          </span>

          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>

        {!isExpanded && (
          <p className="text-xs text-gray-500 mt-1">
            {t(
              "lowScoringPapers.subtitle",
              "These papers have lower relevance scores but may still be useful",
            )}
          </p>
        )}
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div id="low-scoring-papers" className="collapsible-content">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
            {papers.map((paper, idx) => renderPaper(paper, idx))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsibleLowScoringSection;
